import { AddressFeature } from "@dune/types";
import { Input } from "@dune/ui";
import { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDebounce } from "use-debounce";
import { useGetAddresses } from "../../api/queries/address.queries";

interface AddressFormFieldsProps {
	fieldPrefix?: string;
	showBorder?: boolean;
	onGeolocationChange?: (latlon: string) => void;
	includeGeolocation?: boolean;
}

const AddressFormFields = ({
	fieldPrefix = "",
	showBorder = true,
	onGeolocationChange,
	includeGeolocation = false,
}: AddressFormFieldsProps) => {
	const {
		formState: { errors },
		setValue,
		getValues,
	} = useFormContext();

	const getFieldName = useCallback(
		(field: string) => (fieldPrefix ? `${fieldPrefix}.${field}` : field),
		[fieldPrefix],
	);

	const getFieldError = (field: string) => {
		if (!fieldPrefix) return errors[field];

		const prefixParts = fieldPrefix.split(".");
		let errorObj: Record<string, unknown> = errors;

		for (const part of prefixParts) {
			if (errorObj?.[part] && typeof errorObj[part] === "object") {
				errorObj = errorObj[part] as Record<string, unknown>;
			} else {
				return undefined;
			}
		}

		const fieldError = errorObj?.[field];
		return fieldError &&
			typeof fieldError === "object" &&
			"message" in fieldError
			? (fieldError as { message: string })
			: undefined;
	};

	const [streetNumber, setStreetNumber] = useState(
		getValues(getFieldName("streetNumber")) || "",
	);
	const [debouncedStreetNumber] = useDebounce(streetNumber, 500);

	const [streetName, setStreetName] = useState(
		getValues(getFieldName("streetName")) || "",
	);
	const [debouncedStreetName] = useDebounce(streetName, 500);

	const [postalCode, setPostalCode] = useState(
		getValues(getFieldName("postalCode")) || "",
	);
	const [debouncedPostalCode] = useDebounce(postalCode, 500);

	const [city, setCity] = useState(getValues(getFieldName("city")) || "");
	const [debouncedCity] = useDebounce(city, 500);

	const [address, setAddress] = useState(
		getValues(getFieldName("address")) || "",
	);
	const [debouncedAddress] = useDebounce(address, 500);
	const [suggestions, setSuggestions] = useState<AddressFeature[]>([]);

	const { data: addressesFromString } = useGetAddresses(debouncedAddress);

	useEffect(() => {
		setValue(getFieldName("streetNumber"), debouncedStreetNumber);
	}, [debouncedStreetNumber, setValue, getFieldName]);

	useEffect(() => {
		setValue(getFieldName("streetName"), debouncedStreetName);
	}, [debouncedStreetName, setValue, getFieldName]);

	useEffect(() => {
		if (includeGeolocation && !debouncedPostalCode)
			setValue(getFieldName("latlon"), null);
		setValue(getFieldName("postalCode"), debouncedPostalCode);
	}, [debouncedPostalCode, setValue, getFieldName, includeGeolocation]);

	useEffect(() => {
		if (includeGeolocation && !debouncedCity)
			setValue(getFieldName("latlon"), null);
		setValue(getFieldName("city"), debouncedCity);
	}, [debouncedCity, setValue, getFieldName, includeGeolocation]);

	const cityFromForm = getValues(getFieldName("city"));
	useEffect(() => {
		if (cityFromForm) {
			setCity(cityFromForm);
		}
	}, [cityFromForm]);

	const streetNumberFromForm = getValues(getFieldName("streetNumber"));
	useEffect(() => {
		if (streetNumberFromForm) {
			setStreetNumber(streetNumberFromForm);
		}
	}, [streetNumberFromForm]);

	const streetNameFromForm = getValues(getFieldName("streetName"));
	useEffect(() => {
		if (streetNameFromForm) {
			setStreetName(streetNameFromForm);
		}
	}, [streetNameFromForm]);

	const postalCodeFromForm = getValues(getFieldName("postalCode"));
	useEffect(() => {
		if (postalCodeFromForm) {
			setPostalCode(postalCodeFromForm);
		}
	}, [postalCodeFromForm]);

	useEffect(() => {
		if (addressesFromString) {
			setSuggestions(addressesFromString);
		} else {
			setSuggestions([]);
		}
	}, [addressesFromString]);

	const handleSelect = (item: AddressFeature) => {
		setSuggestions([]);
		setAddress("");
		setStreetNumber(item.properties.housenumber ?? "");
		setStreetName(item.properties.street ?? "");
		setPostalCode(item.properties.postcode);
		setCity(item.properties.city);

		if (includeGeolocation) {
			const newLatlon = item.geometry.coordinates.join(",");
			setValue(getFieldName("latlon"), newLatlon);

			if (onGeolocationChange) {
				onGeolocationChange(newLatlon);
			}
		}
	};

	const containerClasses = showBorder
		? "border-solid border-1 border-gray-200 rounded-lg p-4 space-y-4 relative"
		: "space-y-4 relative";

	return (
		<div className={containerClasses}>
			<div>
				<Input
					value={address}
					placeholder="Rechercher une adresse"
					error={getFieldError("address")?.message?.toString()}
					onChange={(e) => {
						setAddress(e.target.value);
					}}
				/>
				{suggestions.length > 0 && (
					<div className="absolute z-10 bg-white border border-gray-300 mt-1 rounded-md p-2 shadow-lg max-h-60 overflow-y-auto">
						{suggestions.map((addressItem) => (
							<div
								key={addressItem.properties.id}
								className="p-2 cursor-pointer hover:bg-gray-100"
								onClick={() => {
									handleSelect(addressItem);
								}}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										handleSelect(addressItem);
									}
								}}
							>
								{addressItem.properties.label}
							</div>
						))}
					</div>
				)}
			</div>
			<div className="flex flex-row gap-4 w-full">
				<Input
					label="Numéro de rue"
					value={streetNumber}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setStreetNumber(e.target.value)
					}
					error={getFieldError("streetNumber")?.message?.toString()}
				/>
				<Input
					label="Nom de rue"
					value={streetName}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setStreetName(e.target.value)
					}
					error={getFieldError("streetName")?.message?.toString()}
				/>
			</div>

			<div className="flex flex-row gap-4 w-full">
				<Input
					label="Code postal"
					value={postalCode}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setPostalCode(e.target.value)
					}
					error={getFieldError("postalCode")?.message?.toString()}
				/>
				<Input
					label="Ville"
					value={city}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setCity(e.target.value)
					}
					error={getFieldError("city")?.message?.toString()}
				/>
			</div>
		</div>
	);
};

export default AddressFormFields;
